import React, { useMemo, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  FaArrowLeft,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaPlay,
  FaRedo,
  FaSlidersH,
  FaUser,
} from "react-icons/fa";

import useScrollToTop from "./../../hooks/useScrollToTop.js";

import ScoreboardUser from "./components/ScoreboardUser/ScoreboardUser.jsx";

const TIME_PRESETS = [
  {
    label: "1 min 30",
    value: 90,
  },
  {
    label: "2 minutes",
    value: 120,
  },
  {
    label: "3 minutes",
    value: 180,
  },
  {
    label: "4 minutes",
    value: 240,
  },
  {
    label: "5 minutes",
    value: 300,
  },
];

const DEFAULT_SETTINGS = {
  temps: 180,
  tapis: "1",

  finIppon: true,
  finDeuxIppon: false,

  finPoints: false,
  pointsFin: 10,

  wazariAwaseteIppon: true,
};

const DEFAULT_FIGHTERS = {
  red: {
    nom_competiteur: "Judoka Rouge",
    prenom_competiteur: "",
    club_competiteur: "",
  },

  white: {
    nom_competiteur: "Judoka Blanc",
    prenom_competiteur: "",
    club_competiteur: "",
  },
};

const formatDuration = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0);

  const minutes = Math.floor(total / 60);

  const remainingSeconds = total % 60;

  if (remainingSeconds === 0) {
    return `${minutes} min`;
  }

  return `${minutes} min ${String(remainingSeconds).padStart(2, "0")}`;
};

const normalizeFighter = (fighter, fallbackName) => {
  return {
    nom_competiteur: fighter?.nom_competiteur?.trim() || fallbackName,

    prenom_competiteur: fighter?.prenom_competiteur?.trim() || "",

    club_competiteur: fighter?.club_competiteur?.trim() || "",
  };
};

export default function ScoreboardUserPage() {
  useScrollToTop();

  const navigate = useNavigate();

  const { sessionId = "public" } = useParams();

  /*
   * Réglages présents dans le formulaire.
   * Ils ne modifient pas le tableau avant
   * que l'utilisateur clique sur Appliquer.
   */
  const [settingsDraft, setSettingsDraft] = useState(() => ({
    ...DEFAULT_SETTINGS,
  }));

  /*
   * Réglages réellement utilisés par
   * le tableau de score.
   */
  const [settings, setSettings] = useState(() => ({
    ...DEFAULT_SETTINGS,
  }));

  const [fightersDraft, setFightersDraft] = useState(() => ({
    red: {
      ...DEFAULT_FIGHTERS.red,
    },

    white: {
      ...DEFAULT_FIGHTERS.white,
    },
  }));

  const [fighters, setFighters] = useState(() => ({
    red: {
      ...DEFAULT_FIGHTERS.red,
    },

    white: {
      ...DEFAULT_FIGHTERS.white,
    },
  }));

  const [showSetupPanel, setShowSetupPanel] = useState(true);

  /*
   * Incrémenter cette valeur force le
   * remontage du tableau lors de
   * l'application d'une nouvelle
   * configuration.
   *
   * Cela remet les scores et les
   * chronomètres à zéro.
   */
  const [scoreboardVersion, setScoreboardVersion] = useState(1);

  const updateSetting = (field, value) => {
    setSettingsDraft((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const updateFighter = (corner, field, value) => {
    setFightersDraft((previous) => ({
      ...previous,

      [corner]: {
        ...previous[corner],
        [field]: value,
      },
    }));
  };

  const applyConfiguration = () => {
    const normalizedSettings = {
      ...settingsDraft,

      temps: Number(settingsDraft.temps) || 180,

      tapis: String(settingsDraft.tapis || "1").trim() || "1",

      pointsFin: Math.max(1, Number(settingsDraft.pointsFin) || 10),
    };

    const normalizedFighters = {
      red: normalizeFighter(fightersDraft.red, "Judoka Rouge"),

      white: normalizeFighter(fightersDraft.white, "Judoka Blanc"),
    };

    setSettings(normalizedSettings);

    setFighters(normalizedFighters);

    /*
     * Nouvelle configuration =
     * nouveau combat.
     */
    setScoreboardVersion((previous) => previous + 1);

    setShowSetupPanel(false);
  };

  const resetDraft = () => {
    setSettingsDraft({
      ...DEFAULT_SETTINGS,
    });

    setFightersDraft({
      red: {
        ...DEFAULT_FIGHTERS.red,
      },

      white: {
        ...DEFAULT_FIGHTERS.white,
      },
    });
  };

  const rulesSummary = useMemo(() => {
    const rules = [];

    if (settings.finIppon) {
      rules.push("Fin sur Ippon");
    }

    if (settings.finDeuxIppon) {
      rules.push("Fin sur 2 Ippons");
    }

    if (settings.wazariAwaseteIppon) {
      rules.push("Waza-ari awasete Ippon");
    }

    if (settings.finPoints) {
      rules.push(`Fin à ${settings.pointsFin} points`);
    }

    if (!rules.length) {
      return "Aucune fin automatique";
    }

    return rules.join(" · ");
  }, [settings]);

  const sessionLabel =
    String(sessionId)
      .replace(/[^\w-]/g, "")
      .slice(0, 30) || "public";

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <main className="mx-auto w-full px-2 pb-4 pt-[120px] sm:px-4 sm:pt-[130px]">
        {/* En-tête */}
        <header className="mx-auto mb-4 max-w-[1800px] overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/95 shadow-2xl shadow-black/30 backdrop-blur">
          <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-500 text-xl text-white shadow-lg shadow-orange-950/30">
                  <FaClock />
                </span>

                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl font-black tracking-tight text-white sm:text-2xl">
                      Mon
                      <span className="text-orange-400">Appli</span>
                      Score
                    </h1>

                    <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-300">
                      Gratuit
                    </span>
                  </div>

                  <p className="mt-1 text-xs text-slate-400 sm:text-sm">
                    Tableau de score de judo individuel
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <SummaryPill>Tapis {settings.tapis}</SummaryPill>

                <SummaryPill>
                  <FaClock />

                  {formatDuration(settings.temps)}
                </SummaryPill>

                <SummaryPill className="border-red-400/20 bg-red-500/10 text-red-200">
                  Rouge : {fighters.red.nom_competiteur}
                </SummaryPill>

                <SummaryPill className="border-white/20 bg-white text-slate-950">
                  Blanc : {fighters.white.nom_competiteur}
                </SummaryPill>

                <SummaryPill>{rulesSummary}</SummaryPill>

                <SummaryPill className="text-slate-400">
                  Session : {sessionLabel}
                </SummaryPill>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setShowSetupPanel((current) => !current)}
                className="inline-flex items-center gap-2 rounded-xl bg-[#004b86] px-4 py-2.5 text-sm font-black text-white shadow-lg shadow-blue-950/30 transition hover:bg-blue-700"
              >
                <FaSlidersH />

                {showSetupPanel ? "Masquer les réglages" : "Modifier le combat"}

                {showSetupPanel ? (
                  <FaChevronUp className="text-xs" />
                ) : (
                  <FaChevronDown className="text-xs" />
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate("/monappliscore")}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-slate-600 hover:bg-slate-800"
              >
                <FaArrowLeft />
                Présentation
              </button>
            </div>
          </div>
        </header>

        {/* Conseil smartphone */}
        <div className="mx-auto mb-4 max-w-[1800px] rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-xs leading-5 text-amber-100 lg:hidden">
          Pour un meilleur confort sur téléphone ou tablette, utilisez
          MonAppliScore en orientation paysage.
        </div>

        {/* Configuration */}
        {showSetupPanel ? (
          <section className="mx-auto mb-4 max-w-[1800px] rounded-3xl border border-slate-800 bg-slate-900 p-4 shadow-2xl shadow-black/30 sm:p-5">
            <div className="mb-5 flex flex-col gap-1">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-400">
                Nouveau combat
              </p>

              <h2 className="text-xl font-black text-white">
                Configuration du tableau
              </h2>

              <p className="text-sm leading-6 text-slate-400">
                L’application d’une nouvelle configuration remettra les scores
                et les chronomètres à zéro.
              </p>
            </div>

            <div className="grid gap-4 xl:grid-cols-[0.55fr_0.75fr_1.25fr_1fr_1fr]">
              {/* Tapis */}
              <ConfigCard title="Numéro du tapis" icon={FaPlay}>
                <input
                  type="text"
                  value={settingsDraft.tapis}
                  onChange={(event) =>
                    updateSetting("tapis", event.target.value)
                  }
                  placeholder="1"
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-3 text-center text-xl font-black text-white outline-none transition focus:border-orange-400"
                />
              </ConfigCard>

              {/* Durée */}
              <ConfigCard title="Durée du combat" icon={FaClock}>
                <select
                  value={settingsDraft.temps}
                  onChange={(event) =>
                    updateSetting("temps", Number(event.target.value))
                  }
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-3 text-sm font-bold text-white outline-none transition focus:border-orange-400"
                >
                  {TIME_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </ConfigCard>

              {/* Règles */}
              <ConfigCard title="Règles du combat" icon={FaSlidersH}>
                <div className="grid gap-2 sm:grid-cols-2">
                  <CheckboxOption
                    checked={settingsDraft.finIppon}
                    label="Fin sur Ippon"
                    onChange={(checked) => {
                      setSettingsDraft((previous) => ({
                        ...previous,

                        finIppon: checked,

                        /*
                         * Les deux options
                         * seraient contradictoires.
                         */
                        finDeuxIppon: checked ? false : previous.finDeuxIppon,
                      }));
                    }}
                  />

                  <CheckboxOption
                    checked={settingsDraft.finDeuxIppon}
                    label="Fin sur 2 Ippons"
                    onChange={(checked) => {
                      setSettingsDraft((previous) => ({
                        ...previous,

                        finDeuxIppon: checked,

                        finIppon: checked ? false : previous.finIppon,
                      }));
                    }}
                  />

                  <CheckboxOption
                    checked={settingsDraft.wazariAwaseteIppon}
                    label="Waza-ari awasete Ippon"
                    onChange={(checked) =>
                      updateSetting("wazariAwaseteIppon", checked)
                    }
                  />

                  <div className="rounded-xl border border-slate-800 bg-slate-950/80 p-3">
                    <CheckboxOption
                      borderless
                      checked={settingsDraft.finPoints}
                      label="Fin sur points"
                      onChange={(checked) =>
                        updateSetting("finPoints", checked)
                      }
                    />

                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-xs text-slate-500">Seuil</span>

                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={settingsDraft.pointsFin}
                        disabled={!settingsDraft.finPoints}
                        onChange={(event) =>
                          updateSetting("pointsFin", Number(event.target.value))
                        }
                        className="w-20 rounded-lg border border-slate-700 bg-black px-2 py-1.5 text-center text-sm font-black text-white outline-none focus:border-orange-400 disabled:cursor-not-allowed disabled:opacity-40"
                      />

                      <span className="text-xs text-slate-500">points</span>
                    </div>
                  </div>
                </div>
              </ConfigCard>

              {/* Rouge */}
              <FighterConfig
                corner="red"
                title="Judoka Rouge"
                fighter={fightersDraft.red}
                onChange={updateFighter}
              />

              {/* Blanc */}
              <FighterConfig
                corner="white"
                title="Judoka Blanc"
                fighter={fightersDraft.white}
                onChange={updateFighter}
              />
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={resetDraft}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm font-bold text-slate-300 transition hover:bg-slate-800"
              >
                <FaRedo />
                Valeurs par défaut
              </button>

              <button
                type="button"
                onClick={applyConfiguration}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-black text-white shadow-lg shadow-orange-950/30 transition hover:bg-orange-400"
              >
                <FaPlay />
                Démarrer ce combat
              </button>
            </div>
          </section>
        ) : null}

        {/* Tableau individuel */}
        <section className="mx-auto max-w-[1800px] overflow-hidden rounded-3xl border border-slate-800 bg-black shadow-2xl shadow-black">
          <ScoreboardUser
            key={`${sessionLabel}-${scoreboardVersion}`}
            settings={settings}
            competiteur1={fighters.red}
            competiteur2={fighters.white}
            soundKey={sessionLabel}
          />
        </section>
      </main>
    </div>
  );
}

function SummaryPill({ children, className = "" }) {
  return (
    <span
      className={[
        "inline-flex",
        "items-center",
        "gap-1.5",
        "rounded-full",
        "border",
        "border-slate-700",
        "bg-slate-950",
        "px-3",
        "py-1.5",
        "font-bold",
        "text-slate-300",
        className,
      ].join(" ")}
    >
      {children}
    </span>
  );
}

function ConfigCard({ title, icon: Icon, children }) {
  return (
    <article className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
      <div className="mb-3 flex items-center gap-2">
        {Icon ? <Icon className="text-sm text-orange-400" /> : null}

        <h3 className="text-sm font-black text-slate-200">{title}</h3>
      </div>

      {children}
    </article>
  );
}

function CheckboxOption({ checked, label, onChange, borderless = false }) {
  return (
    <label
      className={[
        "flex",
        "cursor-pointer",
        "items-center",
        "gap-2",
        "rounded-xl",
        "text-xs",
        "font-bold",
        "text-slate-200",

        borderless ? "" : "border border-slate-800 bg-slate-950/80 px-3 py-2.5",
      ].join(" ")}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-orange-500"
      />

      <span>{label}</span>
    </label>
  );
}

function FighterConfig({ corner, title, fighter, onChange }) {
  const isRed = corner === "red";

  return (
    <article
      className={[
        "rounded-2xl",
        "border",
        "p-4",

        isRed
          ? "border-red-900/60 bg-red-950/20"
          : "border-slate-700 bg-white/[0.04]",
      ].join(" ")}
    >
      <div className="mb-3 flex items-center gap-2">
        <span
          className={[
            "flex",
            "h-8",
            "w-8",
            "items-center",
            "justify-center",
            "rounded-xl",

            isRed ? "bg-red-600 text-white" : "bg-white text-slate-950",
          ].join(" ")}
        >
          <FaUser className="text-sm" />
        </span>

        <h3 className="text-sm font-black text-white">{title}</h3>
      </div>

      <div className="space-y-2">
        <FighterInput
          label="Nom affiché"
          value={fighter.nom_competiteur}
          onChange={(value) => onChange(corner, "nom_competiteur", value)}
          placeholder={isRed ? "Judoka Rouge" : "Judoka Blanc"}
          isRed={isRed}
        />

        <FighterInput
          label="Prénom facultatif"
          value={fighter.prenom_competiteur}
          onChange={(value) => onChange(corner, "prenom_competiteur", value)}
          placeholder="Prénom"
          isRed={isRed}
        />

        <FighterInput
          label="Club facultatif"
          value={fighter.club_competiteur}
          onChange={(value) => onChange(corner, "club_competiteur", value)}
          placeholder="Nom du club"
          isRed={isRed}
        />
      </div>
    </article>
  );
}

function FighterInput({ label, value, onChange, placeholder, isRed }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-black uppercase tracking-wider text-slate-500">
        {label}
      </span>

      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={[
          "w-full",
          "rounded-xl",
          "border",
          "bg-slate-950",
          "px-3",
          "py-2.5",
          "text-sm",
          "font-bold",
          "text-white",
          "outline-none",
          "transition",

          isRed
            ? "border-red-900/60 focus:border-red-400"
            : "border-slate-700 focus:border-orange-400",
        ].join(" ")}
      />
    </label>
  );
}
